import PropTypes                                   from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation }                          from 'react-i18next';
import { useOrderProcess }                         from '@karpeleslab/klb-react-services';
import { rest }                                    from '@karpeleslab/klbfw';
import Grid                                        from '@material-ui/core/Grid';
import Typography                                  from '@material-ui/core/Typography';
import Alert                                       from '@material-ui/lab/Alert';
import CircularProgress                            from '@material-ui/core/CircularProgress';
import QRCode                                      from 'qrcode';
import Button                                      from '../../../../../core/input/Button';
import { useSetSnackbar }                          from '../../../../../../hooks/useSnackbar';

const Crypto = ({ order, settings, setPaying, refreshOrder, setDialogActions = null }) => {
	const { t } = useTranslation();
	const setSnackbar = useSetSnackbar();

	const [processOrder] = useOrderProcess(order.Order__);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [checkoutId, setCheckoutId] = useState(null);
	const [checkoutData, setCheckoutData] = useState(null);
	const [selectedAmount, setSelectedAmount] = useState(null);
	const [timeLeft, setTimeLeft] = useState(null);
	const [isWalletConnecting, setIsWalletConnecting] = useState(false);
	const [walletAccount, setWalletAccount] = useState(null);

	const qrCanvasRef = useRef(null);
	const pollIntervalRef = useRef(null);
	const timerIntervalRef = useRef(null);

	// Cleanup intervals on unmount
	useEffect(() => {
		return () => {
			if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
			if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
		};
	}, []);

	const paymentAddress = selectedAmount?.Payment?.recipient || selectedAmount?.Payment?.to || '';

	const qrCodeData = selectedAmount?.Payment?.qr_code || paymentAddress;

	const isEVM = selectedAmount?.Chain?.Type === 'EVM';

	const hasContractData = !!selectedAmount?.Payment?.data;

	// Initialize: process the order with Crypto method to get checkout ID
	useEffect(() => {
		const init = async () => {
			try {
				setLoading(true);
				setError(null);

				// Get checkout ID from settings or by processing the order
				let cId = settings?.fields?._checkout?.Crypto_Checkout__;
				if (!cId) {
					const process = await processOrder(settings.session, settings.method, {}, { handleError: false });
					if (process?.methods?.Crypto?.fields?._checkout?.Crypto_Checkout__) {
						cId = process.methods.Crypto.fields._checkout.Crypto_Checkout__;
					}
				}

				if (!cId) {
					setError(t('crypto_error_load_failed'));
					setLoading(false);
					return;
				}

				setCheckoutId(cId);
			} catch {
				setError(t('crypto_error_load_failed'));
				setLoading(false);
			}
		};

		init();
	}, []);

	// Once we have a checkoutId, fetch checkout data
	useEffect(() => {
		if (!checkoutId) return;
		loadCheckoutData();
	}, [checkoutId]);

	const loadCheckoutData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await rest('Crypto/Checkout:fetch', 'GET', { id: checkoutId });

			if (!response || !response.data) {
				throw new Error('Failed to load checkout data');
			}

			setCheckoutData(response.data);

			// Auto-select if only one payment option
			if (response.data.Amount && response.data.Amount.length === 1) {
				await selectPaymentMethod(response.data.Amount[0]);
			}

			setupPolling();
		} catch {
			setError(t('crypto_error_load_failed'));
		} finally {
			setLoading(false);
		}
	}, [checkoutId]);

	const selectPaymentMethod = async (amount) => {
		try {
			if (amount.Crypto_Checkout_Amount__) {
				const response = await rest(`Crypto/Checkout/${checkoutId}:activateAmount`, 'POST', {
					amount_id: amount.Crypto_Checkout_Amount__
				});

				if (response?.data) {
					amount = { ...amount, ...response.data };
				}
			}

			setSelectedAmount(amount);

			if (amount.Deadline?.unix) {
				setupTimer(amount.Deadline.unix);
			}
		} catch {
			setError(t('crypto_error_invalid_data'));
		}
	};

	// Generate QR code when selectedAmount changes
	useEffect(() => {
		if (!qrCanvasRef.current || !qrCodeData) return;

		QRCode.toCanvas(qrCanvasRef.current, qrCodeData, {
			width: 200,
			margin: 2,
			color: { dark: '#000000', light: '#FFFFFF' }
		});
	}, [selectedAmount, qrCodeData]);

	const setupPolling = useCallback(() => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}

		pollIntervalRef.current = setInterval(async () => {
			try {
				const response = await rest('Crypto/Checkout:fetch', 'GET', { id: checkoutId });

				if (response?.data) {
					setCheckoutData(response.data);

					if (response.data.Status === 'paid') {
						clearInterval(pollIntervalRef.current);
						pollIntervalRef.current = null;
						if (timerIntervalRef.current) {
							clearInterval(timerIntervalRef.current);
							timerIntervalRef.current = null;
						}

						// Finalize the order
						setSnackbar(t('order_paid'), 'success');
						processOrder(settings.session, settings.method, {}, { handleError: false })
							.then(d => {
								if (d?.order) refreshOrder(d.order);
							});
					}
				}
			} catch {
				// Silently continue polling on error
			}
		}, 5000);
	}, [checkoutId, settings, processOrder, refreshOrder, setSnackbar, t]);

	const setupTimer = (deadline) => {
		if (timerIntervalRef.current) {
			clearInterval(timerIntervalRef.current);
			timerIntervalRef.current = null;
		}

		const updateTimer = () => {
			const now = Math.floor(Date.now() / 1000);
			const diff = deadline - now;

			if (diff <= 0) {
				setTimeLeft(0);
				if (timerIntervalRef.current) {
					clearInterval(timerIntervalRef.current);
					timerIntervalRef.current = null;
				}
				// Reload checkout data when timer expires
				loadCheckoutData();
			} else {
				setTimeLeft(diff);
			}
		};

		updateTimer();
		timerIntervalRef.current = setInterval(updateTimer, 1000);
	};

	const formatAmount = (amount) => {
		if (!amount?.Amount_Full) return '0';
		return amount.Amount_Full.value || amount.Amount_Full.value_disp || '0';
	};

	const formatTime = (seconds) => {
		const minutes = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${minutes}:${secs.toString().padStart(2, '0')}`;
	};

	const truncateAddress = (address, start = 8, end = 6) => {
		if (!address || address.length < start + end) return address;
		return `${address.slice(0, start)}...${address.slice(-end)}`;
	};

	const copyAddress = async () => {
		try {
			await navigator.clipboard.writeText(paymentAddress);
			setSnackbar(t('crypto_copy_success'), 'success');
		} catch {
			setSnackbar(t('crypto_copy_failed'), 'error');
		}
	};

	const handleWalletPayment = async () => {
		if (!window.ethereum) {
			setSnackbar(t('crypto_error_no_wallet'), 'error');
			return;
		}

		try {
			setIsWalletConnecting(true);

			const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
			if (!accounts || accounts.length === 0) {
				throw new Error('No accounts');
			}

			setWalletAccount(accounts[0]);

			const switched = await switchChain();
			if (switched) {
				await triggerPayment(accounts[0]);
			}
		} catch (err) {
			if (err.code === 4001) {
				setSnackbar(t('crypto_error_wallet_rejected'), 'error');
			} else {
				setSnackbar(t('crypto_error_wallet_connection'), 'error');
			}
		} finally {
			setIsWalletConnecting(false);
		}
	};

	const switchChain = async () => {
		if (!window.ethereum || !selectedAmount?.Chain) return false;

		try {
			const chainInfo = selectedAmount.Chain['EIP-3085'];
			if (!chainInfo?.chainId) return true;

			const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
			if (currentChainId === chainInfo.chainId) return true;

			try {
				await window.ethereum.request({
					method: 'wallet_switchEthereumChain',
					params: [{ chainId: chainInfo.chainId }]
				});
				return true;
			} catch (switchError) {
				if (switchError.code === 4902) {
					try {
						await window.ethereum.request({
							method: 'wallet_addEthereumChain',
							params: [{
								chainId: chainInfo.chainId,
								chainName: chainInfo.chainName,
								nativeCurrency: {
									name: chainInfo.nativeCurrency.name,
									symbol: chainInfo.nativeCurrency.symbol,
									decimals: parseInt(chainInfo.nativeCurrency.decimals)
								},
								rpcUrls: chainInfo.rpcUrls,
								blockExplorerUrls: chainInfo.blockExplorerUrls
							}]
						});
						return true;
					} catch {
						setSnackbar(t('crypto_error_chain_add'), 'error');
						return false;
					}
				}
				throw switchError;
			}
		} catch {
			setSnackbar(t('crypto_error_chain_switch'), 'error');
			return false;
		}
	};

	const triggerPayment = async (account) => {
		if (!window.ethereum || !account) return;

		try {
			const transaction = { from: account };

			if (selectedAmount.Payment.to) {
				transaction.to = selectedAmount.Payment.to;
			} else {
				throw new Error('Invalid payment data');
			}

			if (hasContractData) {
				transaction.data = selectedAmount.Payment.data;
			} else if (selectedAmount.Payment.min_balance) {
				transaction.value = selectedAmount.Payment.min_balance;
			} else if (selectedAmount.Amount) {
				transaction.value = selectedAmount.Amount;
			}

			await window.ethereum.request({
				method: 'eth_sendTransaction',
				params: [transaction]
			});

			setSnackbar(t('crypto_transaction_sent'), 'success');
		} catch (err) {
			if (err.code === 4001) {
				setSnackbar(t('crypto_error_wallet_rejected'), 'error');
			} else {
				setSnackbar(t('crypto_error_wallet_signing'), 'error');
			}
		}
	};

	// Initiate the crypto checkout flow
	const handleProcess = async () => {
		setPaying(true);
		try {
			const process = await processOrder(settings.session, settings.method, {}, { handleError: false });
			if (process?.order) refreshOrder(process.order);

			let cId = process?.methods?.Crypto?.fields?._checkout?.Crypto_Checkout__;
			if (cId) setCheckoutId(cId);
		} catch {
			setPaying(false);
		}
	};

	// Dialog actions button
	useEffect(() => {
		if (!setDialogActions) return;

		// Only show the initiate button when still in pending state (before crypto flow started)
		if (order.Status === 'pending' && !checkoutId) {
			setDialogActions(
				<Button
					loading={loading}
					disabled={loading}
					variant='contained'
					color='primary'
					onClick={handleProcess}
				>
					{t('checkout_with_crypto_btn')}
				</Button>
			);
		} else {
			// Clear dialog actions once the crypto flow is active (inline UI takes over)
			setDialogActions(null);
		}
	}, [setDialogActions, loading, order, checkoutId]);

	// Loading state
	if (loading && !checkoutData) {
		return (
			<Grid container spacing={3} justifyContent='center'>
				<Grid item>
					<CircularProgress />
				</Grid>
			</Grid>
		);
	}

	// Error state
	if (error) {
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Alert severity='error'>{error}</Alert>
				</Grid>
			</Grid>
		);
	}

	// Pre-checkout: show initiate button (non-dialog mode)
	if (order.Status === 'pending' && !checkoutId) {
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Alert severity='info'>{t('crypto_payment_pending_message')}</Alert>
				</Grid>
				{!setDialogActions && (
					<Grid item xs={12}>
						<Grid container spacing={3} justifyContent='center'>
							<Grid item>
								<Button
									loading={loading}
									disabled={loading}
									variant='contained'
									color='primary'
									onClick={handleProcess}
								>
									{t('checkout_with_crypto_btn')}
								</Button>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		);
	}

	// Payment received
	if (checkoutData?.Status === 'paid') {
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Alert severity='success'>{t('crypto_payment_received')}</Alert>
				</Grid>
			</Grid>
		);
	}

	// Active checkout
	return (
		<Grid container spacing={3}>
			{/* Amount and currency info */}
			{selectedAmount && (
				<Grid item xs={12}>
					<div style={{ textAlign: 'center', padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
						<Typography variant='h4' color='primary' gutterBottom>
							{formatAmount(selectedAmount)} {selectedAmount.Token?.Symbol || '?'}
						</Typography>
						{checkoutData?.Description && (
							<Typography variant='body2' color='textSecondary'>
								{checkoutData.Description}
							</Typography>
						)}

						{/* Timer */}
						{timeLeft !== null && timeLeft > 0 && (
							<Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
								{t('crypto_expires_in', { time: formatTime(timeLeft) })}
							</Typography>
						)}
					</div>
				</Grid>
			)}

			{/* Stay on page warning */}
			{selectedAmount && (
				<Grid item xs={12}>
					<Alert severity='warning'>{t('crypto_stay_on_page')}</Alert>
				</Grid>
			)}

			{/* Wallet payment button for EVM chains */}
			{selectedAmount && isEVM && timeLeft !== null && timeLeft > 0 && (
				<Grid item xs={12}>
					<Grid container spacing={2} justifyContent='center'>
						<Grid item xs={12} sm={8} md={6}>
							<Button
								variant='contained'
								color='primary'
								fullWidth
								loading={isWalletConnecting}
								disabled={isWalletConnecting}
								onClick={handleWalletPayment}
							>
								{isWalletConnecting
									? t('crypto_wallet_connecting')
									: walletAccount
										? `${t('crypto_wallet_pay')} ${walletAccount.slice(0, 6)}...${walletAccount.slice(-4)}`
										: t('crypto_wallet_pay')
								}
							</Button>
						</Grid>
						<Grid item xs={12}>
							<Typography variant='body2' color='textSecondary' align='center'>
								{t('crypto_or_scan_qr')}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			)}

			{/* QR Code and payment address */}
			{selectedAmount?.Payment && (
				<Grid item xs={12}>
					<Grid container spacing={2} justifyContent='center' alignItems='center' direction='column'>
						<Grid item>
							<div style={{
								padding: 16,
								background: '#fff',
								border: '2px solid #e0e0e0',
								borderRadius: 8,
								display: 'inline-block'
							}}>
								<canvas ref={qrCanvasRef} />
							</div>
						</Grid>
						<Grid item>
							<Button
								variant='outlined'
								onClick={copyAddress}
								style={{ fontFamily: 'monospace', textTransform: 'none' }}
							>
								{truncateAddress(paymentAddress)}
							</Button>
							<Typography variant='caption' display='block' align='center' style={{ marginTop: 4 }}>
								{t('crypto_copy_address')}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={10} md={8}>
							<Alert severity='info'>
								{t('crypto_network_warning', { network: selectedAmount.Chain?.Name || 'selected' })}
							</Alert>
						</Grid>
					</Grid>
				</Grid>
			)}

			{/* Loading currencies */}
			{!selectedAmount && (!checkoutData?.Amount || checkoutData.Amount.length === 0) && (
				<Grid item xs={12}>
					<Grid container spacing={2} justifyContent='center' alignItems='center' direction='column'>
						<Grid item><CircularProgress /></Grid>
						<Grid item>
							<Typography variant='body2' color='textSecondary'>
								{t('crypto_preparing_currencies')}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			)}

			{/* Currency selection */}
			{!selectedAmount && checkoutData?.Amount && checkoutData.Amount.length > 0 && (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						{checkoutData.Amount.map((amount, index) => (
							<Grid item xs={12} key={index}>
								<Button
									variant='outlined'
									fullWidth
									onClick={() => selectPaymentMethod(amount)}
									style={{ justifyContent: 'space-between', textTransform: 'none', padding: '12px 16px' }}
								>
									<span>
										<Typography variant='subtitle1' component='span' style={{ fontWeight: 600 }}>
											{amount.Token?.Symbol || '?'}
										</Typography>
										<Typography variant='caption' component='span' color='textSecondary' style={{ marginLeft: 8 }}>
											{amount.Chain?.Name || ''}
										</Typography>
									</span>
									<Typography variant='subtitle1' component='span' color='primary' style={{ fontWeight: 600 }}>
										{formatAmount(amount)}
									</Typography>
								</Button>
							</Grid>
						))}
					</Grid>
				</Grid>
			)}
		</Grid>
	);
};

export default Crypto;

Crypto.propTypes = {
	refreshOrder: PropTypes.func.isRequired,
	setPaying: PropTypes.func.isRequired,
	settings: PropTypes.object.isRequired,
	order: PropTypes.object.isRequired,
	setDialogActions: PropTypes.func
};
